import styled, { css, keyframes } from 'styled-components'
import { Colors } from 'cio-design-library/design-tokens'
import Typography, { FontWeights } from 'builder/styles/typography'
import Shadows from 'builder/styles/shadows'
import Media from 'builder/styles/media'

const Width = '274px'
const HoverWidth = '572px'
const Height = '291px'

interface CardWrapperProps {
  origin?: 'left' | 'right'
}

export const Card = styled.article<CardWrapperProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 0 0 ${Width};
  width: ${Width};
  height: ${Height};
  gap: 16px;
  background: white;
  cursor: pointer;
  transition: flex-basis 0.3s ease, width 0.3s ease, box-shadow 0.3s ease, border-radius 0.3s ease;

  &:hover {
    ${Shadows.lightWithBorder.mid};
    border-radius: 16px;
    flex-direction: ${({ origin }) => (origin === 'right' ? 'row' : 'row-reverse')};
    width: ${HoverWidth};
    flex-basis: ${HoverWidth};
    padding: 12px;
  }
`

export const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
`

export const Image = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  object-fit: cover;
`

export const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const Footer = styled.div`
  display: none;

  ${Card}:hover & {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`

export const Label = styled.span`
  ${Typography.Caption};
  ${FontWeights.Regular};
  color: ${Colors.Neutral50};
  display: flex;
  align-items: center;
  gap: 4px;
`

export const Title = styled.h2`
  ${Typography.Body};
  ${FontWeights.Medium};
  color: ${Colors.Neutral90};
`

export const Rating = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;

  .stars {
    display: inline-flex;
    align-items: center;
    font-size: 0;
  }

  ${Media.Phone` display: none;`}
`

export const Duration = styled.div`
  display: flex;

  ${Card}:hover & {
    display: flex;
    gap: 8px;
  }
`

export const Review = styled.div<{ type: 'default' | 'primary' }>`
  ${Typography.Body};
  ${FontWeights.Regular};
  color: ${props => (props.type === 'primary' ? Colors.Blue50 : Colors.Neutral50)};
`

export const Description = styled.div`
  ${Typography.Caption};
  ${FontWeights.Regular};
  color: ${Colors.Neutral90};
  display: none;

  ${Card}:hover & {
    display: inline-block;
  }
`

export const Enrolled = styled.div`
  display: none;

  strong {
    ${FontWeights.Medium};
  }

  ${Card}:hover & {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    ${Typography.Caption};
    ${FontWeights.Regular};
    color: ${Colors.Neutral80};
  }
`

export const Badge = styled.div<{
  status: 'recomended' | 'progress'
  progress?: number
  position: 'absolute' | 'relative'
}>`
  ${props =>
    props.position === 'absolute'
      ? `
      position: absolute;
      top: 8px;
      left: 8px;`
      : 'position: relative;'};

  ${props =>
    props.status === 'recomended' &&
    css`
      ${Typography.Caption};
      ${FontWeights.Regular};
      padding: 2px 8px;
      background-color: ${Colors.Blue10};
      color: ${Colors.Blue80};
      border-radius: 2px;
    `}

  ${props =>
    props.status === 'progress' &&
    css`
      width: 40px;
      height: 40px;
      ${Typography.Caption};
      ${FontWeights.Medium};
      color: ${Colors.Neutral90};
      background: ${Colors.White};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: radial-gradient(closest-side, white 79%, transparent 80% 100%),
        conic-gradient(${Colors.Green50} ${props.progress || 0}%, ${Colors.Neutral10} 0);
    `}
`

export const ShowOnHover = styled.div`
  display: none;

  ${Card}:hover & {
    display: block;
  }
`

export const ShowOnHoverVideo = styled.div`
  display: none;
  width: 0;
  height: 0;

  ${Card}:hover & {
    display: block;
    width: 100%;
    height: 100%;
  }
`

export const HideOnHover = styled.div`
  display: block;

  ${Card}:hover & {
    display: none;
  }
`

const shimmer = keyframes`
  0% { background-position: -200px 0 }
  100% { background-position: 200px 0 }
`

const Placeholder = styled.div`
  background: ${Colors.Neutral15};
  background-image: linear-gradient(
    90deg,
    ${Colors.Neutral15} 0px,
    ${Colors.Neutral10} 40px,
    ${Colors.Neutral15} 80px
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 4px;
`

export const ImgSkeleton = styled(Placeholder)`
  width: 100%;
  height: 100%;
  border-radius: 8px;
`

export const LineSkeleton = styled(Placeholder)<{ w: string; h?: string }>`
  width: ${({ w }) => w};
  height: ${({ h }) => h || '12px'};
  border-radius: 4px;
`

export const StarSkeleton = styled(Placeholder)`
  width: 100px;
  height: 16px;
  border-radius: 8px;
`

export const StatSkeleton = styled(Placeholder)`
  width: 60px;
  height: 12px;
`

export const FavoriteButton = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`

export const InitialDisplayLogo = styled.img`
  position: absolute;
  bottom: 8px;
  left: 8px;
`
